import { FC, useState } from 'react';
import { Button, DialogActions, DialogContent, MenuItem, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DATE_FORMATS, Roles } from '@callmart/types';
import { TotalsType } from '../../enums/TotalsType';
import CustomSelect from '../UI/CustomSelect';
import { useCustomStyles } from '../../hooks';
import ResponsiveDialog from '../UI/Dialogs/ResponsiveDialog';
import OSPExportTotalsButton from './OSPExportTotalsButton';
import TEFExportTotalsButton from './TEFExportTotalsButton';
import type { Dayjs } from '@utils/dayjs';

type ExportButtonType = Roles.CallmartTEF | Roles.CallmartOSP;
type TotalsTypeValue = 'bidding' | 'corrected';

const getTotalsTypeOptions = (exportButtonType: ExportButtonType): { value: TotalsTypeValue; name: string }[] => {
    const nameKey = exportButtonType === Roles.CallmartTEF ? 'nameTEF' : 'nameOSP';
    return [
        {
            value: TotalsType.BIDDING.value as 'bidding',
            name: TotalsType.BIDDING[nameKey] ?? 'Bidding Totals',
        },
        {
            value: TotalsType.CORRECTED.value as 'corrected',
            name: TotalsType.CORRECTED[nameKey] ?? 'Corrected Totals',
        },
    ];
};

interface ExportTotalsDialogProps {
    open: boolean;
    onClose: () => void;
    date: Dayjs;
    location?: string | number;
    exportButtonType: ExportButtonType;
}

const ExportTotalsDialog: FC<ExportTotalsDialogProps> = ({ open, onClose, date, location, exportButtonType }) => {
    const { t } = useTranslation();
    const classes = useCustomStyles();
    const dialogTitle = t('bidding.exportTotals') + ' - ' + date.format(DATE_FORMATS.MONTH_YEAR);

    const totalsTypeOptions = getTotalsTypeOptions(exportButtonType);
    const [totalsType, setTotalsType] = useState<TotalsTypeValue>(totalsTypeOptions[0].value);

    return (
        <ResponsiveDialog
            fullScreen={false}
            fullWidth={true}
            title={dialogTitle}
            onClose={onClose}
            open={open}
            actionButtons={
                <DialogActions sx={{ ...classes.dialogActions }}>
                    <Button onClick={onClose} variant="outlined">
                        {t('action.cancel')}
                    </Button>
                    {exportButtonType === Roles.CallmartTEF && (
                        <TEFExportTotalsButton date={date} totalsType={totalsType}></TEFExportTotalsButton>
                    )}
                    {exportButtonType === Roles.CallmartOSP && location && (
                        <OSPExportTotalsButton
                            date={date}
                            location={location}
                            totalsType={totalsType}
                        ></OSPExportTotalsButton>
                    )}
                </DialogActions>
            }
        >
            <DialogContent>
                <Typography sx={{ marginBottom: '1rem' }}>{t('export.whichTotals')}</Typography>
                <CustomSelect<TotalsTypeValue>
                    selectedValue={totalsType}
                    onChange={setTotalsType}
                    label={t('field.totalsType')}
                    options={totalsTypeOptions}
                >
                    {totalsTypeOptions.map(({ value, name }) => (
                        <MenuItem value={value} key={value}>
                            <span className="notranslate">{name}</span>
                        </MenuItem>
                    ))}
                </CustomSelect>
            </DialogContent>
        </ResponsiveDialog>
    );
};

export default ExportTotalsDialog;
